import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InternalLabelData } from "../../../../../models/InternalLabelData";
import DataSvc from "../../../../../services/dataSvc";
import { HookFormSelect } from "../../../../HookForm/Select";
import { AdminLocationAlert } from "../schema";

import "./styles.scss";

/**
 * Handles selecting multiple internal labels. Currently fetches a list of all internal labels at once,
 * but may need to be updated to use a paginated approach if the list becomes too long.
 */
export const InternalLabelSelect = () => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`adminLocationAlertsPage.adminLocationAlertCreateModal.${key}`);

  const internalLabelsQuery = useQuery({
    queryKey: ["internalLabels"],
    queryFn: () => DataSvc.getInternalLabels({ label_only: true }),
  });
  const data: InternalLabelData[] = internalLabelsQuery.data?.data;

  const { watch, setValue } = useFormContext<AdminLocationAlert>();
  const internalLabelIds = watch("internal_label_ids");

  const handleAddInternalLabel = () => {
    setValue("internal_label_ids", [...internalLabelIds, ""]);
  };

  const handleRemoveInternalLabel = (index: number) => {
    const newInternalLabelIds = [...internalLabelIds];
    newInternalLabelIds.splice(index, 1);
    setValue("internal_label_ids", newInternalLabelIds);
  };

  return (
    <div className="internal-label-select">
      <h3>{t("fields.internal_label.label")}</h3>
      {internalLabelIds.map((_id: string, index: number) => (
        <div className="internal-label-select-item" key={index}>
          <div className="internal-label-select-item-select">
            <HookFormSelect
              disabled={!data}
              name={`internal_label_ids.${index}`}
              options={
                data?.map((label: InternalLabelData) => ({
                  label: label.name,
                  value: label.id,
                })) ?? []
              }
              placeholder={data ? undefined : t("loading_internal_labels")}
            />
            {internalLabelIds.length > 1 && (
              <button
                className="btn btn-border btn-border-red remove-internal-label-button"
                type="button"
                onClick={() => handleRemoveInternalLabel(index)}
              >
                {t("fields.internal_label.remove")}
              </button>
            )}
          </div>
          <div className="internal-label-select-item-description">
            {data?.find((label: InternalLabelData) => label.id === _id)
              ?.description ?? ""}
          </div>
        </div>
      ))}
      <button
        className="btn btn-border add-internal-label-button"
        disabled={!data}
        type="button"
        onClick={handleAddInternalLabel}
      >
        {t("fields.internal_label.add")}
      </button>
    </div>
  );
};
